import { makeAutoObservable, reaction } from "mobx";
import { useEffect, useState } from "react";
import { UserService } from "../../services/UserService";
import ApsModels from "../models";
import commonService from "../services/CommonService";
import { GridApi, GridReadyEvent } from "ag-grid-community";

export const ListCredLibraryTypes = [
  {
    value: ApsModels.CredLibraryTypeEnum.All,
    label: "All",
  },
  {
    value: ApsModels.CredLibraryTypeEnum.Aps,
    label: "APS Library",
  },
  {
    value: ApsModels.CredLibraryTypeEnum.Department,
    label: "Agency Library",
  },
];

export interface IDragItem {
  id: string;
  parentId?: string;
  type: "field" | "row" | "col" | "grid";
  copy: any;
  index: number;
}

class SystemStore {
  private _systemMessage: string = "Test System Message";
  private _currentDrillTemplateDepartment: number = 0;
  private _currentDrillTemplate: ApsModels.IDrillTemplateOutputDto =
    null as any;
  private _extraDrillInfo: ApsModels.IDrillOutputDto = null as any;
  private _drillReportFilters = {} as ApsModels.IDrillReportGridInputDto;
  private _systemError: "none" | "access-denied" | "in-token" = "none";
  private _nfirsListFilters: ApsModels.INfirsReportSearchInputDto | null = null;
  private _initialDepartmentIdReady = false;
  private _isoReports: ApsModels.IIsoReportListItemDto[] = [];
  private _newTrainingReport?: ApsModels.ITrainingReportOutputDto;
  private _showNotification = {
    show: false,
    message:
      "The APS website will be closed for scheduled maintenance on September 20th from 3:00 am to 5:00 am Pacific Time. We apologize for any inconvenience.",
  };
  private _has417Error: { message: string; doNothing?: boolean } | null = null;
  private _has408Error: string | null = null;
  private _docDraggedItem?: IDragItem;
  private _docDraggedOverItem?: IDragItem;
  private _docDropped?: boolean;
  private _tmoSystemMessage = 0;
  private _systemBannerHeight = 0;

  constructor() {
    makeAutoObservable(this);
    setInterval(() => {
      this.setTmoSystemMessage(this.tmoSystemMessage + 1);
    }, 30000); //30sec
  }

  get systemMessage() {
    return this._systemMessage;
  }

  get currentDrillTemplateDepartment() {
    return this._currentDrillTemplateDepartment;
  }

  get currentDrillTemplate() {
    return this._currentDrillTemplate;
  }

  get extraDrillInfo() {
    return this._extraDrillInfo;
  }

  get drillReportFilters() {
    const key = `drills-filters-${commonService.currentDepartment}-${commonService.userInfo.userId}`;
    try {
      return JSON.parse(localStorage.getItem(key) || "{}");
    } catch (error) {}
    return this._drillReportFilters;
  }

  get systemError() {
    return this._systemError;
  }

  get xnfirsListFilters() {
    return this._nfirsListFilters;
  }

  get initialDepartmentIdReady() {
    return this._initialDepartmentIdReady;
  }

  get isoReports() {
    return this._isoReports;
  }

  get newTrainingReport() {
    return this._newTrainingReport;
  }

  get showNotification() {
    return this._showNotification;
  }

  get has417Error() {
    return this._has417Error;
  }

  get has408Error() {
    return this._has408Error;
  }

  get docDraggedItem() {
    return this._docDraggedItem;
  }

  get docDraggedOverItem() {
    return this._docDraggedOverItem;
  }

  get docDropped() {
    return this._docDropped;
  }

  get tmoSystemMessage() {
    return this._tmoSystemMessage;
  }

  get systemBannerHeight() {
    return this._systemBannerHeight;
  }

  setSystemMessage(msg: string) {
    this._systemMessage = msg;
  }

  setCurrentDrillTemplateDepartment(id: number) {
    this._currentDrillTemplateDepartment = id;
  }

  setCurrentDrillTemplate(temp: ApsModels.IDrillTemplateOutputDto) {
    this._currentDrillTemplate = temp;
  }

  setExtraDrillInfo(val: ApsModels.IDrillOutputDto) {
    this._extraDrillInfo = val;
  }

  setDrillReportFilters(data: ApsModels.IDrillReportGridInputDto) {
    const key = `drills-filters-${commonService.currentDepartment}-${commonService.userInfo.userId}`;
    this._drillReportFilters = data;
    localStorage.setItem(key, JSON.stringify(data));
  }

  showSystemError(val: "none" | "access-denied" | "in-token") {
    //Will only set first "none" value
    if (this._systemError === "none") {
      this._systemError = val;
    } else if (val === "none") {
      this._systemError = val;
    }
  }

  setxNfirsListFilters(data: ApsModels.INfirsReportSearchInputDto | null) {
    this._nfirsListFilters = data;
  }

  setInitialDepartmentIdReady() {
    this._initialDepartmentIdReady = true;
  }

  setIsoReports(reports: ApsModels.IIsoReportListItemDto[]) {
    this._isoReports = [...reports];
  }

  setNewTrainingReport(data?: ApsModels.ITrainingReportOutputDto) {
    this._newTrainingReport = data;
  }

  setShowNotification(show: boolean, message: string) {
    this._showNotification = {
      show,
      message,
    };
  }

  setHas417Error(message: string | null, doNothing = false) {
    if (!message) {
      this._has417Error = null;
    } else {
      this._has417Error = {
        message: message,
        doNothing: doNothing,
      };
    }
  }

  setHas408Error(message: string | null) {
    return (this._has408Error = message);
  }

  setDocDraggedItem(item?: IDragItem) {
    if (
      item &&
      item.id === this._docDraggedItem?.id &&
      item.type === this._docDraggedItem?.type
    ) {
      return;
    }
    this._docDraggedItem = item;
  }

  setDocDraggedOverItem(item?: IDragItem) {
    if (
      item &&
      item.id === this._docDraggedOverItem?.id &&
      item.type === this._docDraggedOverItem?.type
    ) {
      return;
    }
    this._docDraggedOverItem = item;
    if (!item) {
      this._docDropped = false;
    }
  }

  setDocDropped(dropped?: boolean) {
    this._docDropped = dropped;
  }

  setTmoSystemMessage(tmo: number) {
    this._tmoSystemMessage = tmo > 100 ? 1 : tmo;
  }

  setSystemBannerHeight(val: number) {
    this._systemBannerHeight = val;
  }
}

const systemStore = new SystemStore();
export default systemStore;

/**
 * Use to detect department selection.
 * @returns
 */
export const useDepartmentId = () => {
  const [initDepReady, setInitDepReady] = useState(
    systemStore.initialDepartmentIdReady,
  );
  const [departmentId, setDepartmentId] = useState({
    current: initDepReady ? UserService.currentDepartment.get() : 0,
    previous: initDepReady ? UserService.currentDepartment.get() : 0,
    changed: false,
  });
  useEffect(() => {
    const disposer = UserService.currentDepartment.subscribe((id) => {
      setDepartmentId((data) => {
        return {
          current: id,
          previous: data.current,
          changed: id !== data.current,
        };
      });
    });

    const disposer2 = reaction(
      () => systemStore.initialDepartmentIdReady,
      (n, p, i) => {
        if (!initDepReady && n) {
          setDepartmentId({
            current: UserService.currentDepartment.get(),
            previous: UserService.currentDepartment.get(),
            changed: false,
          });
        }
        setInitDepReady(n);
      },
    );

    return () => {
      disposer();
      disposer2();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return departmentId;
};

export const useShowNotification = () => {
  const [showNotification, setShowNotification] = useState(
    systemStore.showNotification,
  );

  useEffect(() => {
    const disposer = reaction(
      () => systemStore.showNotification,
      (n, p, i) => {
        setShowNotification(n);
      },
    );

    return () => {
      disposer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return showNotification;
};

export const useNewTrainingReport = () => {
  const [newTrainingReport, setNewTrainingReport] = useState(
    systemStore.newTrainingReport,
  );

  useEffect(() => {
    const disposer = reaction(
      () => systemStore.newTrainingReport,
      (n, p, i) => {
        setNewTrainingReport(n);
      },
    );

    return () => {
      disposer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return newTrainingReport;
};

export const useGridFilters = (
  key: string,
  gridApi: GridApi,
  init: (others: { [key: string]: any }) => void,
  dontSaveLocally = false,
  onChange?: () => void,
  fileName?: string,
) => {
  const [initial, setInitial] = useState(false);
  const props = {
    ...commonService.agGridHelpers.onStateChanges(
      key,
      dontSaveLocally,
      onChange,
      fileName,
    ),
  };
  const [others, setOthers] = useState<{ [key: string]: any }>();
  const [ready, setReady] = useState(false);
  const otherKey = props._key + "_other";
  useEffect(() => {
    setOthers(JSON.parse(localStorage.getItem(otherKey) || "{}"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gridApi && !initial && others) {
      setInitial(true);
      init(others);
      setReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, others]);

  const [gridEvent, setGridEvent] = useState<GridReadyEvent>();
  const reset = (params: GridReadyEvent) => {
    localStorage.removeItem(props._key);
    //localStorage.removeItem(otherKey);
    if (params) {
      params.columnApi.resetColumnState();
      params.api.setFilterModel({});
      params.api.refreshToolPanel();
    }
    commonService.agResizeColumns(params, true);
  };

  return {
    others,
    ready,
    reset() {
      gridEvent && reset(gridEvent);
    },
    resize() {
      gridEvent && commonService.agResizeColumns(gridEvent);
    },
    saveOthers(data: { [key: string]: any }) {
      localStorage.setItem(otherKey, JSON.stringify(data));
      setOthers(others);
    },
    reApplyFilters() {
      setTimeout(() => {
        //need to reapply filter after adding more data
        if (gridEvent) {
          props._reApplyFilters(gridEvent);
          //gridEvent.api.hideOverlay();
        }
      }, 100);
    },
    init: (params: GridReadyEvent) => {
      setGridEvent(params);
      commonService.agGridHelpers.resetFlag(props._key);
      //params.api.closeToolPanel();
      params.api.addEventListener("customResetColumnsAndFilters", () => {
        params && reset(params);
      });
      return props._init(params);
    },
    exportCSV: (all = false) => {
      const keys = (
        gridEvent?.columnApi
          ?.getAllDisplayedColumns()
          .map((column) => column.getColId()) || []
      ).filter((f) => {
        const col = gridEvent?.columnApi?.getColumn(f);
        if (col && col.getColDef()?.suppressColumnsToolPanel) {
          return false;
        }
        return true;
      });
      gridEvent?.api?.exportDataAsCsv({
        onlySelected: !all,
        onlySelectedAllPages: !all,
        columnKeys: keys,
        fileName: commonService.cleanFileName(fileName || key),
      });
    },
    exportAllCSV: () => {
      const keys = (
        gridEvent?.columnApi
          ?.getAllColumns()
          .map((column) => column.getColId()) || []
      ).filter((f) => {
        const col = gridEvent?.columnApi?.getColumn(f);
        if (col && col.getColDef()?.suppressColumnsToolPanel) {
          return false;
        }
        return true;
      });
      const filters = gridEvent?.api?.getFilterModel();
      gridEvent?.api?.setFilterModel({});
      gridEvent?.api?.showLoadingOverlay();
      setTimeout(() => {
        gridEvent?.api?.exportDataAsCsv({
          onlySelected: false,
          onlySelectedAllPages: false,
          columnKeys: keys,
          fileName: commonService.cleanFileName(fileName || key),
        });
        gridEvent?.api?.setFilterModel(filters);
        gridEvent?.api?.hideOverlay();
      }, 500);
    },
    props: {
      ...props,
      sideBar: {
        ...commonService.agGridHelpers.sidebar,
      },
    },
  };
};

export const useDocDragEvents = () => {
  const [draggedItem, setDraggedItem] = useState(systemStore.docDraggedItem);
  const [draggedOverItem, setDraggedOverItem] = useState(
    systemStore.docDraggedOverItem,
  );
  const [dropped, setDropped] = useState(systemStore.docDropped);

  useEffect(() => {
    const disposer = reaction(
      () => systemStore.docDraggedItem,
      (n, p, i) => {
        setDraggedItem(n);
      },
    );

    const disposer1 = reaction(
      () => systemStore.docDraggedOverItem,
      (n, p, i) => {
        setDraggedOverItem(n);
      },
    );

    const disposer2 = reaction(
      () => systemStore.docDropped,
      (n, p, i) => {
        setDropped(n);
      },
    );

    return () => {
      disposer();
      disposer1();
      disposer2();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isValidDragOver = (currentDraggedOver: IDragItem) => {
    if (draggedItem && currentDraggedOver) {
      if (
        draggedItem.type === "field" &&
        !["field", "col"].includes(currentDraggedOver.type)
      ) {
        return false;
      } else if (
        draggedItem.type === "col" &&
        !["row", "col"].includes(currentDraggedOver.type)
      ) {
        return false;
      } else if (
        draggedItem.type === "row" &&
        !["row", "grid"].includes(currentDraggedOver.type)
      ) {
        return false;
      } else if (
        draggedItem.type === "grid" &&
        !["field", "col"].includes(currentDraggedOver.type)
      ) {
        return false;
      }
      if (
        document.getElementById(currentDraggedOver.id)?.closest(".active-drag")
      ) {
        //console.log("Cannot drop to children");
        return false;
      }
      return true;
    }
  };

  return {
    draggedItem,
    draggedOverItem,
    dropped,
    reset: () => {
      systemStore.setDocDraggedItem(undefined);
      systemStore.setDocDraggedOverItem(undefined);
      systemStore.setDocDropped(false);
    },
    events: (data: IDragItem) => {
      return {
        onDragEnd: (e: DragEvent) => {
          e.preventDefault();
          systemStore.setDocDraggedOverItem(undefined);
        },
        // onDragLeave: (e: DragEvent) => {},
        onDragOver: (e: DragEvent) => {
          if (!draggedItem) return;
          if (draggedItem?.parentId === data.id) return;
          e.preventDefault();
          if (isValidDragOver(data)) {
            e.stopPropagation();
            systemStore.setDocDraggedOverItem(data);
          }
        },
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          if (
            draggedItem &&
            draggedOverItem &&
            isValidDragOver(draggedOverItem)
          ) {
            //console.log("dropppedd....");
            systemStore.setDocDropped(true);
          }
        },
      };
    },
  };
};

export const useSystemMessageTimer = () => {
  const [tmoSystemMessage, setTmoSystemMessage] = useState(
    systemStore.tmoSystemMessage,
  );

  useEffect(() => {
    const disposer = reaction(
      () => systemStore.tmoSystemMessage,
      (n, p, i) => {
        setTmoSystemMessage(n);
      },
    );

    return () => {
      disposer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return tmoSystemMessage;
};

export const useSystemBannerHeight = () => {
  const [systemBannerHeight, setSystemBannerHeight] = useState(
    systemStore.systemBannerHeight,
  );

  useEffect(() => {
    const disposer = reaction(
      () => systemStore.systemBannerHeight,
      (n, p, i) => {
        setSystemBannerHeight(n);
      },
    );

    return () => {
      disposer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return systemBannerHeight;
};
